import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../firebaseconfig';
import { fetchCategories } from '../utils/categories';
import { 
  FaQuestion,
  FaUtensils,
  FaGraduationCap,
  FaShoppingBasket,
  FaLightbulb,
  FaTint,
  FaUsers,
  FaCoffee,
  FaWineGlass,
  FaWater,
  FaToolbox,
  FaHandHoldingWater,
  FaHandHoldingHeart
} from 'react-icons/fa';
import './Dashboard.css';

const Dashboard = () => {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeRange, setTimeRange] = useState('week');

  useEffect(() => {
    //alert("Dashboard");
    const q = query(collection(db, 'expenses'), orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      try {
        const expensesData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setExpenses(expensesData);
        setLoading(false);
      } catch (err) {
        setError('Error loading expenses');
        setLoading(false);
        console.error('Error fetching expenses:', err);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const fetched = await fetchCategories();
        setCategories(fetched);
      } catch (e) {
        console.error('Error loading categories:', e);
        setError('Error loading categories');
      }
    };
    loadCategories();
  }, []);

  const getFilteredExpenses = () => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    
    return expenses.filter(expense => {
      if (!expense.date && !expense.createdAt) return false;
      
      /*const expenseDate = new Date(
        (expense.date?.toDate?.() || expense.createdAt?.toDate?.()) ?? null
      );*/
      //const expenseDate = new Date(expense.createdAt);
      const expenseDateRaw = expense.date || expense.createdAt;
      const expenseDate = expenseDateRaw?.toDate?.() ?? new Date(expenseDateRaw);

      switch (timeRange) {
        case 'today':
          return expenseDate >= startOfToday && expenseDate <= endOfToday;
        case 'week':
          const weekAgo = new Date(now);
          weekAgo.setDate(now.getDate() - 7);
          return expenseDate >= weekAgo;
        case 'month':
          const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
          return expenseDate >= startOfMonth;
        case 'year':
          const startOfYear = new Date(now.getFullYear(), 0, 1);
          return expenseDate >= startOfYear;
        default:
          return true;
      }
    });
  };

  const calculateTotalAmount = (expensesList) => {
    return expensesList.reduce((total, expense) => total + (Number(expense.amount) || 0), 0);
  };

  const calculateCategoryTotals = (expensesList) => {
    return expensesList.reduce((totals, expense) => {
      if (!expense.category) return totals;
      const categoryId = expense.category;
      totals[categoryId] = (totals[categoryId] || 0) + (Number(expense.amount) || 0);
      return totals;
    }, {});
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(amount || 0);
  };

  const getTopCategories = (expensesList) => {
    const categoryTotals = calculateCategoryTotals(expensesList);
    return Object.entries(categoryTotals)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3)
      .map(([categoryId, amount]) => {
        const category = categories.find(c => c.id === categoryId) || { id: categoryId, name: 'Other', color: '#95A5A6', icon: '📦' };
        return {
          ...category,
          amount,
          key: `${categoryId}_${amount}`
        };
      });
  };

  const getRecentExpenses = (expensesList) => {
    return expensesList
      .filter(expense => expense && expense.id && expense.amount)
      .slice(0, 5);
  };

  const getTimeRangeLabel = () => {
    switch (timeRange) {
      case 'today':
        return 'Today';
      case 'week':
        return 'This Week';
      case 'month':
        return 'This Month';
      case 'year':
        return 'This Year';
      default:
        return '';
    }
  };

  const availableIcons = [
    { name: 'FaQuestion', icon: <FaQuestion /> },
    { name: 'FaUtensils', icon: <FaUtensils /> },
    { name: 'FaGraduationCap', icon: <FaGraduationCap /> },
    { name: 'FaShoppingBasket', icon: <FaShoppingBasket /> },
    { name: 'FaLightbulb', icon: <FaLightbulb /> },
    { name: 'FaTint', icon: <FaTint /> },
    { name: 'FaUsers', icon: <FaUsers /> },
    { name: 'FaCoffee', icon: <FaCoffee /> },
    { name: 'FaWineGlass', icon: <FaWineGlass /> },
    { name: 'FaWater', icon: <FaWater /> },
    { name: 'FaToolbox', icon: <FaToolbox /> },
    { name: 'FaHandHoldingWater', icon: <FaHandHoldingWater /> },
    { name: 'FaHandHoldingHeart', icon: <FaHandHoldingHeart /> }
  ];

  const renderCategoryIcon = (category) => {
    if (!category || !category.icon) return <FaQuestion />;
    const match = availableIcons.find(i => i.name === category.icon);
    return match ? match.icon : <FaQuestion />;
  };

  const filteredExpenses = getFilteredExpenses();
  const totalAmount = calculateTotalAmount(filteredExpenses);
  const topCategories = getTopCategories(filteredExpenses);
  const recentExpenses = getRecentExpenses(expenses);

  if (loading) {
    return <div className="loading">Loading dashboard...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h2>Expenses Dashboard</h2>
        <div className="time-range-filter">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="time-range-select"
          >
            {/*<option value="today">Today</option>*/}
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="year">This Year</option>
          </select>
        </div>
      </div>

      <div className="dashboard-grid">
        <div className="dashboard-card total-expenses">
          <h3>Total Expenses</h3>
          <p className="amount">{formatAmount(totalAmount)}</p>
          <p className="period">{getTimeRangeLabel()}</p>
        </div>

        <div className="dashboard-card top-categories">
          <h3>Top Categories</h3>
          <div className="category-list">
            {topCategories.map(category => (
              <div key={category.key} className="category-item">
                <div className="category-icon" style={{ backgroundColor: category.color || '#95A5A6' }}>
                  {renderCategoryIcon(category)}
                </div>
                <div className="category-details">
                  <p className="category-name">{category.name || 'Other'}</p>
                  <p className="category-amount">{formatAmount(category.amount)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="dashboard-card category-distribution">
          <h3>Item Distribution</h3>
          <div className="distribution-bars">
            {topCategories.map(category => {
              const percentage = totalAmount > 0 ? (category.amount / totalAmount) * 100 : 0;
              return (
                <div key={category.key} className="distribution-bar-container">
                  <div className="distribution-label">
                    <span>{category.name || 'Other'}</span>
                    <span>{Math.round(percentage)}%</span>
                  </div>
                  <div className="distribution-bar">
                    <div
                      className="distribution-fill"
                      style={{
                        width: `${percentage}%`,
                        backgroundColor: category.color || '#95A5A6'
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="dashboard-card recent-expenses">
          <h3>Recent Expenses</h3>
          <div className="recent-list">
            {recentExpenses.map(expense => {
              const category = categories.find(c => c.id === (expense.category || 'other')) || { id: 'other', name: 'Other', color: '#95A5A6', icon: '📦' };
              return (
                <div key={expense.id} className="recent-item">
                  <div className="recent-icon" style={{ backgroundColor: category.color || '#95A5A6' }}>
                    {renderCategoryIcon(category)}
                  </div>
                  <div className="recent-details">
                    <p className="recent-category">{category.name || 'Other'}</p>
                  </div>
                  <p className="recent-amount">{formatAmount(expense.amount)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 